import { useEffect, useState } from "react";
import { Timeline, Card, Image, Typography, Tag, Table } from "antd";
import { TTicketDetail } from "../../../types";
import useFetch from "../../../hooks/useFetch";
import { eResultCode } from "../../../utils/enum";
import { TResponseModel } from "../../../types/config";
import { GetSpecificTicket } from "../../../utils/api.constant";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import Loader from "../../../components/loader/loader";
import styles from "./ticket.module.css";
import { eTICKETSTATUS, eTICKETSTATUSCOLORS } from "../../../utils/enum";
import DateUtils from "../../../utils/date";
import { dateFormat } from "../../../utils/constants";
import { getStatusColor } from "../../../utils/ticket-status";
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  ExclamationOutlined,
  EyeOutlined,
  Loading3QuartersOutlined,
  MenuOutlined,
  PlayCircleOutlined,
  PushpinOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import CustomModal from "../../../components/modal";
import { LiveLocation } from "../../../components/location";

const { Text } = Typography;

type TProps = {
  ticketId?: number;
  ticketDetails?: TTicketDetail[];
};

const ServiceRequestHistory = ({ ticketId, ticketDetails }: TProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLocation, setShowLocation] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    latitude: number;
    longitude: number;
  }>();
  const [view, setView] = useState<string>("table");
  const [ticketDetailList, setTicketDetailList] = useState<TTicketDetail[]>([]);
  const { post } = useFetch();
  const { onShowToast } = useToast();

  useEffect(() => {
    fetchSpecificTicket();
    ticketDetails?.length && setTicketDetailList(ticketDetails);
  }, [ticketId, ticketDetails]);

  const getFileExtension = (url: string) => {
    const path = url.split("?")[0];
    const parts = path.split(".");
    return parts.length > 1 ? parts[parts.length - 1] : null;
  };

  const initialColumns = [
    {
      title: "Updated By",
      dataIndex: "updatedByName",
      key: "updatedByName",
      render: (text: string, detail: TTicketDetail) => (
        <>
          <span>{text || "N/A"}</span>
          {detail?.geolocation && (
            <PushpinOutlined
              style={{
                marginLeft: "5px",
                color: "var(--bs-primary)",
                fontSize: "18px",
                cursor: "pointer",
              }}
              onClick={() => {
                setSelectedLocation(
                  JSON.parse((detail?.geolocation as any) || "")
                );
                setShowLocation(true);
              }}
            />
          )}
        </>
      ),
      width: "7%",
    },
    {
      title: "Updated On",
      dataIndex: "createdOn",
      key: "createdOn",
      render: (text: string) => (
        <span>{text ? DateUtils.format(text, dateFormat) : "--"}</span>
      ),
      width: "7%",
    },
    {
      title: "Status",
      dataIndex: "statusName",
      key: "statusName",
      render: (text: string) => (
        <Tag color={getStatusColor(text)}>{text.toLocaleUpperCase()}</Tag>
      ),
      width: "10%",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "10%",
    },
    {
      title: "Documents",
      dataIndex: "documentList",
      key: "documentList",
      width: "8%",
      render: (documents: any[]) => (
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {documents?.map((doc) => {
            const extension = getFileExtension(doc.path);
            const isVideo = extension === "mp4" || extension === "mov";
            return isVideo ? (
              <div
                key={doc.id}
                onClick={() => window.open(doc.path, "_blank")}
                style={{
                  position: "relative",
                  width: 30,
                  height: 30,
                  borderRadius: "5px",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
              >
                <video
                  src={doc.path}
                  width={30}
                  height={30}
                  style={{ objectFit: "cover", borderRadius: "5px" }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "white",
                    fontSize: "15px",
                    pointerEvents: "none",
                  }}
                >
                  <PlayCircleOutlined />
                </div>
              </div>
            ) : (
              <Image
                key={doc.id}
                src={doc.path}
                alt={`Document ${doc.id}`}
                width={30}
                height={30}
                style={{ objectFit: "cover", borderRadius: "5px" }}
                preview={{
                  mask: (
                    <>
                      <EyeOutlined />
                    </>
                  ),
                }} // Hide preview text
              />
            );
          })}
        </div>
      ),
    },
  ];

  const fetchSpecificTicket = async () => {
    if (ticketId) {
      try {
        const payload = {
          data: {
            id: ticketId,
          },
        };
        setIsLoading(true);
        const response: TResponseModel = await post(GetSpecificTicket, payload);
        const { data, dataResponse } = response;
        if (dataResponse.returnCode === eResultCode.SUCCESS) {
          setTicketDetailList(data?.ticketDetailList);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const mapStatusToEnum = (status: string | undefined): eTICKETSTATUS => {
    if (!status) return eTICKETSTATUS.UNASSIGNED;
    const upperCasedStatus = status.toLocaleUpperCase();
    return Object.values(eTICKETSTATUS).includes(
      upperCasedStatus as eTICKETSTATUS
    )
      ? (upperCasedStatus as eTICKETSTATUS)
      : eTICKETSTATUS.UNASSIGNED;
  };

  const getStatusIcon = (status: eTICKETSTATUS) => {
    switch (status) {
      case eTICKETSTATUS.INPROGRESS:
        return (
          <Loading3QuartersOutlined
            style={{ fontSize: "20px", color: eTICKETSTATUSCOLORS.INPROGRESS }}
          />
        );
      case eTICKETSTATUS.VISITSCHEDULED:
        return (
          <ClockCircleOutlined
            style={{
              fontSize: "20px",
              color: eTICKETSTATUSCOLORS.VISITSCHEDULED,
            }}
          />
        );
      case eTICKETSTATUS.HOLDDELIVERYDEPENDENCY:
        return (
          <ExclamationOutlined
            style={{
              fontSize: "20px",
              color: eTICKETSTATUSCOLORS.HOLDDELIVERYDEPENDENCY,
            }}
          />
        );
      case eTICKETSTATUS.HOLDSPAREDEPENDENCY:
        return (
          <ExclamationOutlined
            style={{
              fontSize: "20px",
              color: eTICKETSTATUSCOLORS.HOLDSPAREDEPENDENCY,
            }}
          />
        );
      case eTICKETSTATUS.CLOSE:
        return (
          <CloseCircleOutlined
            style={{ fontSize: "20px", color: eTICKETSTATUSCOLORS.CLOSE }}
          />
        );
      case eTICKETSTATUS.UNASSIGNED:
        return (
          <UserAddOutlined
            style={{ fontSize: "20px", color: eTICKETSTATUSCOLORS.UNASSIGNED }}
          />
        );
      case eTICKETSTATUS.SERVICEENGINEERASSIGNED:
        return (
          <CheckCircleOutlined
            style={{
              fontSize: "20px",
              color: eTICKETSTATUSCOLORS.SERVICEENGINEERASSIGNED,
            }}
          />
        );
      default:
        return (
          <ExclamationOutlined
            style={{ fontSize: "20px", color: eTICKETSTATUSCOLORS.DEFAULT }}
          />
        );
    }
  };

  return (
    <div className={styles.cardContainer}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: ".5rem",
          position: "sticky",
          top: 0,
          backgroundColor: "white",
          zIndex: 1000,
          padding: "10px 0",
        }}
      >
        <MenuOutlined
          style={{
            fontSize: "1.2rem",
            cursor: "pointer",
            color: view === "table" ? "var(--bs-primary)" : "inherit",
          }}
          onClick={() => setView("table")}
        />
        <AppstoreOutlined
          style={{
            fontSize: "1.2rem",
            cursor: "pointer",
            marginLeft: "1rem",
            color: view === "card" ? "var(--bs-primary)" : "inherit",
          }}
          onClick={() => setView("card")}
        />
      </div>
      {isLoading ? (
        <Loader />
      ) : view === "table" ? (
        <Table
          columns={initialColumns}
          dataSource={ticketDetailList}
          rowKey="id"
          pagination={false}
          style={{ overflowX: "auto" }}
          sticky
          scroll={{
            x: "max-content", // Horizontal scrolling
            y: "400px", // Adjust height dynamically
          }}
        />
      ) : (
        <Timeline style={{ margin: "50px 40px" }}>
          {ticketDetailList?.map((record) => (
            <Timeline.Item
              style={{
                height:
                  Array.isArray(record.documentList) &&
                  record.documentList.length > 0
                    ? "50%"
                    : "20%",
              }}
              key={record.id}
              dot={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    gap: "5px",
                  }}
                >
                  {getStatusIcon(mapStatusToEnum(record.statusName))}
                </div>
              }
            >
              <Card bordered={false} className={styles.card}>
                <Tag
                  style={{ margin: "15px 0px" }}
                  color={getStatusColor(record.statusName)}
                >
                  {record.statusName?.toLocaleUpperCase()}
                </Tag>
                <br />
                <Text className={styles.description}>{record.description}</Text>
                <br />

                <div className={styles.box}>
                  <Text className={styles.reason}>
                    {record.statusReasonName}
                  </Text>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    margin: " 8px 0px",
                  }}
                >
                  <div>
                    <UserOutlined
                      style={{ color: "grey", marginRight: "5px" }}
                    />
                    <Text className={styles.reason}>Updated By </Text>{" "}
                    <Text className={styles.reason}>
                      {record?.updatedByName || "N/A"}
                    </Text>
                    <span className={styles.reason}> on </span>
                    <Text className={styles.reason}>
                      {record.createdOn
                        ? DateUtils.format(record.createdOn, dateFormat)
                        : "--"}
                    </Text>
                    {record?.geolocation && (
                      <PushpinOutlined
                        style={{
                          marginLeft: "5px",
                          color: "var(--bs-primary)",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setSelectedLocation(
                            JSON.parse((record?.geolocation as any) || "")
                          );
                          setShowLocation(true);
                        }}
                      />
                    )}
                  </div>
                </div>
                {Array.isArray(record.documentList) &&
                  record.documentList.length > 0 && (
                    <div
                      style={{
                        marginTop: "1rem",
                        marginBottom: "1rem",
                        backgroundColor: "white",
                        padding: "10px",
                        borderRadius: "5px",
                      }}
                    >
                      <Text className={styles.reasons}>Documents:</Text>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          flexWrap: "wrap",
                          marginTop: "0.5rem",
                        }}
                      >
                        {record.documentList.map((doc) => {
                          const extension = getFileExtension(doc.path);
                          const isVideo =
                            extension === "mp4" || extension === "mov";
                          return isVideo ? (
                            <div
                              key={doc.id}
                              onClick={() => window.open(doc.path, "_blank")}
                              style={{
                                position: "relative",
                                width: 100,
                                height: 100,
                                borderRadius: "5px",
                                overflow: "hidden",
                                cursor: "pointer",
                              }}
                            >
                              <video
                                src={doc.path}
                                width={100}
                                height={100}
                                style={{
                                  objectFit: "cover",
                                  borderRadius: "5px",
                                }}
                              />
                              <div
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  color: "white",
                                  fontSize: "24px",
                                  pointerEvents: "none",
                                }}
                              >
                                <PlayCircleOutlined />
                              </div>
                            </div>
                          ) : (
                            <Image
                              key={doc.id}
                              src={doc.path}
                              alt={`Document ${doc.id}`}
                              width={100}
                              height={100}
                              style={{
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}
              </Card>
            </Timeline.Item>
          ))}
        </Timeline>
      )}
      <CustomModal
        title="Engineer Location"
        isOpen={showLocation}
        okText="Apply"
        hideOkButton={true}
        hideCancelButton={true}
        onOk={() => {}}
        onCancel={() => {
          setShowLocation(false);
        }}
      >
        <LiveLocation geoLocation={selectedLocation}></LiveLocation>
      </CustomModal>
    </div>
  );
};

export default ServiceRequestHistory;
